"use client";

import { GroupedOptionLabelValue, OptionLabelValue } from "@/types/input";
import { cn } from "@/utils/cn";
import { ChevronDown, X } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { Button, Label, ListBox, ListBoxItem, MenuTrigger, Popover, Text } from "react-aria-components";

type SelectInputProps = {
	label?: string;
	description?: string;
	errorMessage?: string;
	options: (GroupedOptionLabelValue | OptionLabelValue)[];
	placeholder?: string;
	className?: string;
	multiple?: boolean;
	clearable?: boolean;
	value?: string[];
	onChange?: (value: string[]) => void;
	disabled?: boolean;
	variant?: "outline" | "underline";
	maxVisibleTags?: number;
};

function SelectInput({
	label,
	description,
	errorMessage,
	options,
	placeholder = "Select an option",
	className,
	multiple = false,
	clearable = false,
	value,
	onChange,
	disabled = false,
	variant = "outline",
	maxVisibleTags = 1, // how many chips to show before "+N more"
}: SelectInputProps) {
	const [selectedValues, setSelectedValues] = useState<string[]>(value || []);

	// Check if options are grouped
	const isGrouped = useMemo(() => {
		return options.length > 0 && "options" in options[0];
	}, [options]);

	// Flatten grouped options for easier searching
	const flatOptions = useMemo(() => {
		if (isGrouped) {
			return (options as GroupedOptionLabelValue[]).flatMap(group => group.options);
		}
		return options as OptionLabelValue[];
	}, [options, isGrouped]);

	const handleSelect = useCallback(
		(optionValue: string) => {
			const isAlreadySelected = selectedValues.includes(optionValue);
			let newArray: string[] = [];

			if (isAlreadySelected) {
				newArray = selectedValues.filter(v => v !== optionValue);
			} else {
				newArray = multiple ? [...selectedValues, optionValue] : [optionValue];
			}

			setSelectedValues(newArray);
			onChange?.(newArray);
		},
		[selectedValues, multiple, onChange],
	);

	const handleClear = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			setSelectedValues([]);
			onChange?.([]);
		},
		[onChange],
	);

	const displayedValues = useMemo(() => {
		if (selectedValues.length <= maxVisibleTags) {
			return selectedValues;
		}
		return selectedValues.slice(0, maxVisibleTags);
	}, [selectedValues, maxVisibleTags]);

	const extraCount = selectedValues.length > maxVisibleTags ? selectedValues.length - maxVisibleTags : 0;

	const renderOptions = () => {
		if (isGrouped) {
			const items: React.ReactNode[] = [];
			(options as GroupedOptionLabelValue[]).forEach(group => {
				// Add a group header (disabled item)
				items.push(
					<ListBoxItem
						key={`header-${group.value}`}
						id={`header-${group.value}`}
						className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-200 mb-1 cursor-default"
						isDisabled
					>
						{group.label}
					</ListBoxItem>,
				);

				// Add group options
				group.options.forEach(opt => {
					const isSelected = selectedValues.includes(opt.value);
					items.push(
						<ListBoxItem
							key={`${group.value}-${opt.value}`}
							id={opt.value}
							onAction={() => handleSelect(opt.value)}
							className={cn(
								"cursor-pointer rounded px-2 py-2 text-body flex items-center justify-between ml-2",
								"hover:bg-gray-100 focus:bg-gray-100 outline-none",
								isSelected && "bg-primary-100 text-primary-700",
							)}
						>
							{opt.label}
							{isSelected && <span className="pl-1 text-primary-600">✔</span>}
						</ListBoxItem>,
					);
				});
			});
			return items;
		} else {
			return (options as OptionLabelValue[]).map(opt => {
				const isSelected = selectedValues.includes(opt.value);
				return (
					<ListBoxItem
						key={opt.value}
						id={opt.value}
						onAction={() => handleSelect(opt.value)}
						className={cn(
							"cursor-pointer rounded px-2 py-2 text-body flex items-center justify-between",
							"hover:bg-gray-100 focus:bg-gray-100 outline-none",
							isSelected && "bg-primary-100 text-primary-700",
						)}
					>
						{opt.label}
						{isSelected && <span className="pl-1 text-primary-600">✔</span>}
					</ListBoxItem>
				);
			});
		}
	};

	return (
		<div className={cn("flex flex-col gap-2 py-3", className)}>
			{label && <Label className="text-title-medium">{label}</Label>}

			<MenuTrigger>
				<div className="relative w-full">
					{/* Trigger button */}
					<Button
						className={cn(
							"flex items-center justify-between px-3 h-12 text-body w-full cursor-pointer",
							"disabled:opacity-50 disabled:cursor-not-allowed",
							errorMessage ? "border-red-500" : "border-gray-300",
							variant === "outline"
								? "border rounded-lg focus:border-primary-600 focus:ring-2 focus:ring-primary-100"
								: "border-0 border-b focus:border-b-1 focus:border-primary-600", // underline variant
						)}
						isDisabled={disabled}
					>
						<div className="flex flex-wrap gap-1 truncate">
							{selectedValues.length > 0 ? (
								<>
									{displayedValues.map((val: string) => {
										const optLabel = flatOptions?.find(o => o.value === val)?.label || val;
										return (
											<span key={optLabel} className="px-2 py-1 text-xs bg-primary-100 text-primary-700 rounded-md">
												{optLabel}
											</span>
										);
									})}
									{extraCount > 0 && (
										<span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-md">+{extraCount} more</span>
									)}
								</>
							) : (
								<span className="text-gray-400">{placeholder}</span>
							)}
						</div>
						<ChevronDown size={16} aria-hidden="true" />
					</Button>

					{/* Clear button placed separately, not inside the trigger */}
					{clearable && selectedValues.length > 0 && (
						<button
							aria-label="Clear selection"
							type="button"
							onClick={handleClear}
							className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
						>
							<X size={16} />
						</button>
					)}

					{/* Popover */}
					{((isGrouped && (options as GroupedOptionLabelValue[]).some(group => group.options.length > 0)) ||
						(!isGrouped && options.length > 0)) && (
						<Popover className="border border-gray-200 rounded-md shadow-lg bg-white p-1 mt-1">
							<ListBox className="outline-none max-h-60 overflow-y-auto scrollbar-thin">{renderOptions()}</ListBox>
						</Popover>
					)}
				</div>
			</MenuTrigger>

			{description && (
				<Text slot="description" className="text-body-small text-gray-500">
					{description}
				</Text>
			)}

			{errorMessage && (
				<Text slot="errorMessage" className="text-body-small-medium text-destructive">
					{errorMessage}
				</Text>
			)}
		</div>
	);
}

export { SelectInput, type GroupedOptionLabelValue, type OptionLabelValue };
